import React, { useEffect } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { toast } from '@src/helpers/toaster'
import { isAxiosError } from 'axios'

import { useGetMyAccount } from '@queries/Account'
import { useAcceptInvite, useGetInviteData } from '@queries/Teams'
import routes from '@src/Routes/routes'
import { loadPending, pendingConsentSearch } from '@pages/McpConsent/pendingConnection'
import { requestErrorsHandler } from '../utils'

const Auth = (Cmp: React.ComponentType) => {
  const Authenticated = (props: React.ComponentProps<typeof Cmp>) => {
    const { data: user, isInitialLoading, isSuccess: isLoggedIn, refetch: getAccount } = useGetMyAccount()
    const { mutateAsync: acceptInvite } = useAcceptInvite()
    const onboarded = !!user?.team?.onboarded
    const navigate = useNavigate()

    const location = useLocation()
    const { search, state, pathname } = location

    const query = new URLSearchParams(search)
    const invite = query.get('invite') as string

    const { isFetched: isInviteFetched, error: inviteCodeError } = useGetInviteData(invite || undefined)

    const handleInviteCheck = async () => {
      try {
        if (!inviteCodeError) {
          await acceptInvite({ invite })
          navigate(pathname, { replace: true })
          toast.success('You successfully joined to the Team')
          await getAccount()
        } else {
          const status = inviteCodeError?.response?.status
          if (status === 404) {
            toast.error('Invite code does not exist')
          } else if (status !== 400) {
            // 400 = invite already used (new user who signed up via invite link) — ignore silently
            requestErrorsHandler(inviteCodeError)
          }
          navigate(pathname, { replace: true })
        }
      } catch (err) {
        if (isAxiosError(err) && err?.response?.status !== 500) {
          requestErrorsHandler(err)
        }
      }
    }

    useEffect(() => {
      if (!!user && invite && isInviteFetched) {
        handleInviteCheck()
      }
    }, [user, isInviteFetched, inviteCodeError])

    if (isLoggedIn) {
      // A connection someone started before they had an account, resumed.
      // Placed here rather than in the login form because the two paths
      // that need it end differently — sign-in lands on the dashboard,
      // sign-up lands here again after onboarding — and both pass through
      // this guard. Checked only once onboarded, so a brand-new user
      // finishes setting up their workspace before being asked to grant
      // an application access to it.
      //
      // `pendingConsentSearch` rebuilds the query from the stored fields
      // rather than replaying a saved URL, so nothing that was not one of
      // the known authorization parameters can come back through it.
      if (onboarded && pathname !== routes.public.mcpConsent) {
        const pending = loadPending()
        if (pending) {
          // Read but NOT cleared here. Clearing is a side effect, and a
          // render can be abandoned or replayed — under StrictMode it runs
          // twice — so a request removed during render can be gone without
          // the navigation that consumed it ever committing. The consent
          // page clears it on mount instead, once arriving is a fact.
          return <Navigate to={{ pathname: routes.public.mcpConsent, search: pendingConsentSearch(pending) }} replace />
        }
      }

      if (!onboarded && !pathname.startsWith('/onboarding')) {
        return (
          <Navigate
            to={{
              pathname: '/onboarding',
            }}
            state={{ ...(state || {}), prev: pathname }}
          />
        )
      }

      return <Cmp {...props} />
    }

    if (!isLoggedIn && !isInitialLoading) {
      return (
        <Navigate
          to={{
            pathname: invite ? routes.public.signup : routes.public.login,
            search,
          }}
          state={{ ...(state || {}), prev: pathname }}
        />
      )
    }

    return null
  }

  return Authenticated
}

export default Auth
